// src/user/PrivacyPolicy.js
import React from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PolicyIcon from "@mui/icons-material/Policy";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import DiamondIcon from "@mui/icons-material/Diamond";
import AppsIcon from "@mui/icons-material/Apps";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LockIcon from "@mui/icons-material/Lock";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

import { portal, portalGradientTextSx } from "../styles/dashboardBrand";
import DashboardPageHeader from "../components/DashboardPageHeader";

/** Reusable section card with a gradient accent bar — same vibe as Home dashboard cards. */
function PolicySectionCard({ icon: Icon, title, accent = portal.cyan, children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: alpha(isDark ? accent : theme.palette.divider, isDark ? 0.22 : 0.12),
        background: isDark
          ? `linear-gradient(165deg, ${alpha("#1a1f30", 0.97)} 0%, ${alpha("#12151f", 0.94)} 45%, ${alpha("#0c0e14", 0.98)} 100%)`
          : alpha(theme.palette.background.paper, 0.98),
        backdropFilter: "blur(16px)",
        boxShadow: isDark
          ? `0 10px 36px ${alpha("#000", 0.4)}, inset 0 1px 0 ${alpha("#fff", 0.04)}, 0 0 32px ${alpha(accent, 0.06)}`
          : `0 10px 32px ${alpha(theme.palette.primary.main, 0.06)}`,
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${accent} 0%, ${portal.indigo} 50%, ${portal.pink} 100%)`,
          opacity: isDark ? 0.85 : 0.7,
        },
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: alpha(accent, isDark ? 0.45 : 0.3),
          boxShadow: isDark
            ? `0 16px 44px ${alpha("#000", 0.45)}, 0 0 30px ${alpha(accent, 0.18)}`
            : `0 14px 36px ${alpha(accent, 0.16)}`,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.75 }, pt: { xs: 2.25, sm: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${alpha(accent, isDark ? 0.22 : 0.16)} 0%, ${alpha(portal.indigo, isDark ? 0.18 : 0.1)} 100%)`,
              border: "1px solid",
              borderColor: alpha(accent, isDark ? 0.35 : 0.25),
              boxShadow: isDark ? `0 0 16px ${alpha(accent, 0.2)}` : "none",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: accent, fontSize: 22 }} />
          </Box>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 800,
              letterSpacing: "0.02em",
              fontSize: { xs: "1rem", sm: "1.1rem" },
              color: isDark ? "common.white" : "text.primary",
            }}
          >
            {title}
          </Typography>
        </Stack>
        <Divider sx={{ mb: 1.75, borderColor: alpha(theme.palette.divider, isDark ? 0.4 : 0.12) }} />
        <Stack spacing={1.25} sx={{ color: "text.secondary", lineHeight: 1.7 }}>
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}

function PolicyBullet({ children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.2}>
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          mt: "0.55em",
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: portal.gradientBar,
          boxShadow: isDark ? `0 0 10px ${alpha(portal.cyan, 0.55)}` : `0 0 6px ${alpha(portal.cyan, 0.3)}`,
        }}
        aria-hidden
      />
      <Typography variant="body2" sx={{ fontSize: { xs: "0.9rem", sm: "0.95rem" }, lineHeight: 1.7 }}>
        {children}
      </Typography>
    </Stack>
  );
}

export default function PrivacyPolicy() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const lastUpdated = "May 11, 2026";

  return (
    <Container maxWidth="md" sx={{ py: { xs: 1.5, sm: 3 }, px: { xs: 1, sm: 2 } }}>
      <DashboardPageHeader
        icon={PolicyIcon}
        title="Privacy & App Policy"
        subtitle="What your coins, rubies & diamonds are — and how you can earn rewards with us."
      />

      {/* Hero / banner */}
      <Card
        elevation={0}
        sx={{
          position: "relative",
          mb: 3,
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid",
          borderColor: alpha(isDark ? portal.cyan : theme.palette.divider, isDark ? 0.28 : 0.16),
          background: isDark
            ? `linear-gradient(135deg, ${alpha("#1a2138", 0.96)} 0%, ${alpha("#1d1428", 0.95)} 50%, ${alpha("#240f1c", 0.96)} 100%)`
            : `linear-gradient(135deg, ${alpha(portal.cyan, 0.06)} 0%, ${alpha(portal.purple, 0.05)} 60%, ${alpha(portal.pink, 0.05)} 100%)`,
          boxShadow: isDark
            ? `0 16px 48px ${alpha("#000", 0.45)}, 0 0 48px ${alpha(portal.cyan, 0.08)}`
            : `0 14px 40px ${alpha(portal.cyan, 0.12)}`,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.indigo} 35%, ${portal.purple} 65%, ${portal.pink} 100%)`,
            opacity: isDark ? 0.95 : 0.85,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 88% 20%, ${alpha(portal.pink, isDark ? 0.18 : 0.08)} 0%, transparent 55%)`,
            pointerEvents: "none",
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2.25, sm: 3.25 }, pt: { xs: 2.75, sm: 3.5 }, position: "relative", zIndex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
            <VerifiedUserIcon sx={{ color: portal.cyan, fontSize: 24, filter: isDark ? `drop-shadow(0 0 10px ${alpha(portal.cyan, 0.5)})` : "none" }} />
            <Typography
              variant="overline"
              sx={{
                letterSpacing: "0.16em",
                fontWeight: 700,
                fontSize: "0.7rem",
                ...portalGradientTextSx({}, `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.pink} 100%)`),
              }}
            >
              Your trust matters
            </Typography>
          </Stack>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.25rem", sm: "1.6rem" },
              letterSpacing: "-0.01em",
              mb: 1.25,
              color: isDark ? "common.white" : "text.primary",
            }}
          >
            Coins, rubies &amp; diamonds are{" "}
            <Box
              component="span"
              sx={portalGradientTextSx(
                { fontWeight: 900 },
                `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.indigo} 50%, ${portal.pink} 100%)`
              )}
            >
              100% virtual
            </Box>
            .
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDark ? alpha("#fff", 0.78) : "text.secondary",
              fontSize: { xs: "0.92rem", sm: "1rem" },
              lineHeight: 1.7,
              maxWidth: 720,
            }}
          >
            Your <strong>Dreamey Coins</strong>, <strong>Rubies</strong> and <strong>Diamonds</strong> are virtual
            rewards that live across our family of apps. You can{" "}
            <strong>earn real rewards through redemptions</strong> by inviting new members and growing your referral
            network.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "inline-block",
              mt: 1.75,
              px: 1.25,
              py: 0.4,
              borderRadius: 999,
              border: "1px solid",
              borderColor: alpha(portal.cyan, isDark ? 0.35 : 0.25),
              background: alpha(portal.cyan, isDark ? 0.1 : 0.06),
              color: portal.cyan,
              fontWeight: 700,
              letterSpacing: "0.06em",
              fontSize: "0.7rem",
            }}
          >
            LAST UPDATED · {lastUpdated.toUpperCase()}
          </Typography>
        </CardContent>
      </Card>

      <Stack spacing={2.25}>
        <PolicySectionCard icon={DiamondIcon} title="Everything in-app is virtual" accent={portal.cyan}>
          <PolicyBullet>
            <strong>Dreamey Coins (DC)</strong>, <strong>Rubies</strong> and <strong>Diamonds</strong> shown across
            your dashboard are <strong>in-app virtual rewards</strong>.
          </PolicyBullet>
          <PolicyBullet>
            They are not real currency by themselves and cannot be transferred between users outside our official
            redemption flow.
          </PolicyBullet>
        </PolicySectionCard>

        <PolicySectionCard icon={AppsIcon} title="Use them across our apps" accent={portal.purple}>
          <PolicyBullet>
            Your virtual balances follow you across our apps — including <strong>Dreamey Vids</strong>,{" "}
            <strong>Dreamey Hub</strong>, and the games we are building next.
          </PolicyBullet>
          <PolicyBullet>
            Diamonds are virtual game currency and are used inside games and in-app experiences only.
          </PolicyBullet>
        </PolicySectionCard>

        <PolicySectionCard icon={LockIcon} title="Your data &amp; privacy" accent={portal.indigo}>
          <PolicyBullet>
            We only collect what we need to run your account — for example, your name, email, referral code and (when
            you redeem) verification details.
          </PolicyBullet>
          <PolicyBullet>
            We do not sell your personal data to third parties. You can update your details any time from{" "}
            <strong>My Profile</strong>.
          </PolicyBullet>
        </PolicySectionCard>

        <PolicySectionCard icon={EmailOutlinedIcon} title="Contact" accent={portal.rose}>
          <PolicyBullet>
            We may update this policy from time to time. The date at the top of this page will always reflect the
            most recent revision.
          </PolicyBullet>
        </PolicySectionCard>
      </Stack>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 3,
          textAlign: "center",
          color: "text.secondary",
          opacity: 0.85,
          fontStyle: "italic",
        }}
      >
        Thanks for being part of Dreamey. Your virtual rewards make our worlds feel alive.
      </Typography>
    </Container>
  );
}
